'use client'

import { Add, ArrowBack, ArrowForward, Close, History } from '@mui/icons-material'
import { IconButton, ListItemIcon, ListItemText, Menu, MenuItem } from '@mui/material'
import { styled } from '@mui/material/styles'
import { useTranslations } from 'next-intl'
import { usePathname, useRouter, useSearchParams } from 'next/navigation'
import type { KeyboardEvent as ReactKeyboardEvent, MouseEvent as ReactMouseEvent } from 'react'
import { useEffect, useMemo, useRef, useState } from 'react'
import { useDispatch } from 'react-redux'

import useRecentActivity from '~/components/QuickSearch/hooks/useRecentActivity'
import { categoryIcons } from '~/components/QuickSearch/icons'
import { isInAppPath, normalizeAppPath } from '~/navigation/isInAppPath'

import { type SynopDesktopBridge, getNavigationApi, getSynopDesktop } from './bridge'
import { readPersistedTabs, writePersistedTabs } from './tabsPersistence'
import {
  activeTabClosed,
  activeTabNavigated,
  activeTabTitleChanged,
  historyTraversed,
  nextTabActivated,
  previousTabActivated,
  tabActivated,
  tabAtIndexActivated,
  tabClosed,
  tabOpened,
  tabsRestored,
  useDesktopTabs
} from './tabsSlice'
import useDesktopCriticalNotifications from './useDesktopCriticalNotifications'
import useDesktopFleetStatus from './useDesktopFleetStatus'

/**
 * Vertical-alignment invariant with the shell: synop-desktop-app positions
 * the macOS traffic lights at `trafficLightPosition: { x: 16, y: 18 }`, and
 * the lights are ~12px tall, so their visual center sits at y ≈ 24px. The
 * bar's flex-centered content must share that center line, so the height is
 * 2 × 24 = 48. If the shell's y offset ever changes, change this with it.
 */
const TITLE_BAR_HEIGHT = 48
/** The lights span x 16–68; leave clear air before the first control. */
const TRAFFIC_LIGHT_INSET = 100

/** What Cmd+T / the + button opens — the app's landing page. */
const NEW_TAB_PATH = '/dashboard/'

/**
 * Desktop-only window chrome, rendered when the app runs inside the Synop
 * desktop shell (Linear-style: the web app draws its own title bar). Provides
 * back/forward over the real browser history — which the Next.js App Router
 * already drives — a recently-viewed dropdown reusing the Cmd+K visit
 * tracker, and client-side tabs (one webcontents; each tab remembers its
 * route). Also feeds the shell's tray icon and dock badge from the merged
 * notifications feed.
 *
 * Calling `setNavigationState` is also what tells the shell this page handles
 * desktop chrome, so menu accelerators, trackpad swipes, deep links, and
 * intercepted window.open calls arrive as commands here instead of the shell
 * driving webContents navigation directly.
 *
 * Renders nothing in a browser — and, deliberately, runs none of the data
 * hooks there either: the bridge check gates the content component so browser
 * sessions (and existing tests that render the layout tree) pay zero cost.
 */
function DesktopTitleBar() {
  const bridge = useMemo(() => getSynopDesktop(), [])
  if (!bridge) return null
  return <DesktopTitleBarContent bridge={bridge} />
}

export default DesktopTitleBar

type DesktopTitleBarContentProps = {
  bridge: SynopDesktopBridge
}

function DesktopTitleBarContent({ bridge }: DesktopTitleBarContentProps) {
  const t = useTranslations('features.DesktopTitleBar')
  const router = useRouter()
  const dispatch = useDispatch()
  // Re-read history state and recents after every client-side navigation.
  // Pathname alone drops search params (charger ?tab=, list ?mode=, filters);
  // the stored tab route has to round-trip the full URL the page is on.
  const pathname = usePathname()
  const searchParams = useSearchParams()
  const route = currentRoute(pathname, searchParams)
  const routeRef = useRef(route)
  routeRef.current = route
  // history.back()/forward() fire popstate before Next.js commits the new
  // route. Remember the destination so the sync effect can tell a traverse
  // from in-tab navigation (see historyTraversed).
  const historyTraversalPathRef = useRef<string | null>(null)
  const { getRecentItems } = useRecentActivity()
  const { tabs, activeTabId } = useDesktopTabs()
  const tabsRef = useRef(tabs)
  const activeTabIdRef = useRef(activeTabId)
  tabsRef.current = tabs
  activeTabIdRef.current = activeTabId
  const [menuAnchor, setMenuAnchor] = useState<HTMLElement | null>(null)
  const [historyState, setHistoryState] = useState({ canGoBack: false, canGoForward: false })

  useDesktopFleetStatus(bridge)
  useDesktopCriticalNotifications()

  // Restore the previous session's tabs, reconciled against the route the
  // window opened on. Runs before the navigation-tracking effect below (hook
  // order), so a cold-start deep link stays put.
  useEffect(() => {
    dispatch(tabsRestored({ tabs: readPersistedTabs(), currentPath: routeRef.current }))
  }, [dispatch])

  useEffect(() => {
    const onPopState = () => {
      historyTraversalPathRef.current = currentRoute(
        window.location.pathname,
        new URLSearchParams(window.location.search)
      )
    }
    window.addEventListener('popstate', onPopState)
    return () => window.removeEventListener('popstate', onPopState)
  }, [])

  // Resolve the active tab's label from document.title (set per page by
  // usePageTitle). Apply only when document.title itself changes: a
  // pathname/tab switch leaves the previous page's title in place, and
  // writing that over an already-correct stored label corrupts the tab.
  const lastDocumentTitleRef = useRef<string | null>(null)
  useEffect(() => {
    if (historyTraversalPathRef.current === route) {
      historyTraversalPathRef.current = null
      dispatch(historyTraversed(route))
      return
    }
    historyTraversalPathRef.current = null
    const active = tabsRef.current.find((tab) => tab.id === activeTabIdRef.current)
    const pathnameChanged = Boolean(
      active && pathWithoutSearch(active.path) !== pathWithoutSearch(route)
    )
    dispatch(activeTabNavigated(route))
    if (pathnameChanged) {
      lastDocumentTitleRef.current = null
    }
  }, [dispatch, route])

  useEffect(() => {
    const applyTitle = () => {
      const raw = document.title
      if (raw === lastDocumentTitleRef.current) return
      lastDocumentTitleRef.current = raw
      const title = cleanDocumentTitle(raw)
      if (title) dispatch(activeTabTitleChanged(title))
    }
    applyTitle()
    const observer = new MutationObserver(applyTitle)
    observer.observe(document.head, { childList: true, characterData: true, subtree: true })
    return () => observer.disconnect()
  }, [dispatch])

  useEffect(() => {
    if (tabs.length > 0) writePersistedTabs(tabs)
  }, [tabs])

  // Switching tabs (activate/close/cycle) navigates to the tab's stored
  // route. Organic navigation keeps the active tab's path equal to the
  // current route, so this only fires on real tab changes.
  useEffect(() => {
    const active = tabs.find((tab) => tab.id === activeTabId)
    if (!active || active.path === routeRef.current) return
    blurActiveElement()
    router.push(active.path)
  }, [tabs, activeTabId, router])

  useEffect(() => {
    const navigationApi = getNavigationApi()
    if (!navigationApi) return
    const update = () =>
      setHistoryState({ canGoBack: navigationApi.canGoBack, canGoForward: navigationApi.canGoForward })
    update()
    navigationApi.addEventListener('currententrychange', update)
    return () => navigationApi.removeEventListener('currententrychange', update)
  }, [route])

  useEffect(() => {
    void bridge.setNavigationState?.({ ...historyState, tabCount: Math.max(1, tabs.length) })
  }, [bridge, historyState, tabs.length])

  useEffect(() => {
    const unsubscribers = [
      bridge.onNavCommand?.((command) => (command === 'back' ? goBack() : goForward())),
      bridge.onOpenRoute?.((route) => {
        // Routes originate in `synop://` deep links any web page can trigger.
        if (!isInAppPath(route)) return
        dispatch(tabOpened(normalizeAppPath(route)))
      }),
      bridge.onTabCommand?.((command) => {
        if (command === 'new-tab') dispatch(tabOpened(NEW_TAB_PATH))
        if (command === 'close-tab') dispatch(activeTabClosed())
        if (command === 'next-tab') dispatch(nextTabActivated())
        if (command === 'previous-tab') dispatch(previousTabActivated())
      })
    ]
    return () => unsubscribers.forEach((unsubscribe) => unsubscribe?.())
  }, [bridge, dispatch])

  // Cmd+1…8 select a tab by position; Cmd+9 selects the last (the browser
  // convention). Plain Cmd chords only — digits never need Shift here, which
  // sidesteps the AZERTY digit/Shift trap (see comboUtils).
  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      if (!event.metaKey || event.ctrlKey || event.altKey || event.shiftKey) return
      if (!/^[1-9]$/.test(event.key)) return
      event.preventDefault()
      dispatch(tabAtIndexActivated({ index: Number(event.key) - 1, last: event.key === '9' }))
    }
    window.addEventListener('keydown', onKeyDown)
    return () => window.removeEventListener('keydown', onKeyDown)
  }, [dispatch])

  const recentItems = menuAnchor ? getRecentItems() : []

  const openRecentItem = (path: string) => {
    setMenuAnchor(null)
    blurActiveElement()
    router.push(path)
  }

  const activateTab = (tabId: string) => {
    if (tabId !== activeTabId) dispatch(tabActivated(tabId))
  }

  const onTabKeyDown = (event: ReactKeyboardEvent, tabId: string) => {
    if (event.key !== 'Enter' && event.key !== ' ') return
    event.preventDefault()
    activateTab(tabId)
  }

  const onTabAuxClick = (event: ReactMouseEvent, tabId: string) => {
    // Middle-click closes, the browser convention.
    if (event.button === 1 && tabs.length > 1) dispatch(tabClosed(tabId))
  }

  return (
    <Bar>
      <NoDragIconButton size="small" aria-label={t('back')} disabled={!historyState.canGoBack} onClick={goBack}>
        <ArrowBack fontSize="small" />
      </NoDragIconButton>
      <NoDragIconButton
        size="small"
        aria-label={t('forward')}
        disabled={!historyState.canGoForward}
        onClick={goForward}
      >
        <ArrowForward fontSize="small" />
      </NoDragIconButton>
      <NoDragIconButton
        size="small"
        aria-label={t('recentlyViewed')}
        onClick={(event) => setMenuAnchor(event.currentTarget)}
      >
        <History fontSize="small" />
      </NoDragIconButton>

      <TabsRow role="tablist">
        {tabs.map((tab) => (
          <TabChip
            key={tab.id}
            role="tab"
            tabIndex={0}
            aria-selected={tab.id === activeTabId}
            active={tab.id === activeTabId}
            onClick={() => activateTab(tab.id)}
            onKeyDown={(event) => onTabKeyDown(event, tab.id)}
            onAuxClick={(event) => onTabAuxClick(event, tab.id)}
          >
            <TabLabel>{tab.title ?? fallbackTabTitle(tab.path)}</TabLabel>
            {tabs.length > 1 ? (
              <TabCloseButton
                size="small"
                aria-label={t('closeTab')}
                onClick={(event) => {
                  event.stopPropagation()
                  dispatch(tabClosed(tab.id))
                }}
              >
                <Close sx={{ fontSize: 14 }} />
              </TabCloseButton>
            ) : null}
          </TabChip>
        ))}
        <NoDragIconButton size="small" aria-label={t('newTab')} onClick={() => dispatch(tabOpened(NEW_TAB_PATH))}>
          <Add fontSize="small" />
        </NoDragIconButton>
      </TabsRow>

      <Menu
        anchorEl={menuAnchor}
        open={Boolean(menuAnchor)}
        onClose={() => setMenuAnchor(null)}
        slotProps={{ paper: { sx: { minWidth: 280, maxWidth: 420 } } }}
      >
        {recentItems.length === 0 ? (
          <MenuItem disabled>{t('noRecentPages')}</MenuItem>
        ) : (
          recentItems.map((item) => (
            <MenuItem key={`${item.category}:${item.id}`} onClick={() => openRecentItem(item.path)}>
              <ListItemIcon>{categoryIcons[item.category]}</ListItemIcon>
              <ListItemText
                primary={item.label}
                secondary={item.subLabel}
                primaryTypographyProps={{ noWrap: true }}
                secondaryTypographyProps={{ noWrap: true }}
              />
            </MenuItem>
          ))
        )}
      </Menu>
    </Bar>
  )
}

function goBack() {
  blurActiveElement()
  window.history.back()
}

function goForward() {
  blurActiveElement()
  window.history.forward()
}

/**
 * Keyboard-initiated navigation leaves a stale `:focus-visible` ring on
 * whatever element the browser falls back to after the page unmounts — see
 * `useKeyboardShortcuts` for the original pattern.
 */
function blurActiveElement() {
  const active = document.activeElement
  if (active instanceof HTMLElement) active.blur()
}

/**
 * usePageTitle writes "<Tenant> - <Page> - <Detail>"; a tab label wants the
 * part after the constant tenant prefix.
 */
function cleanDocumentTitle(raw: string): string | null {
  const parts = raw.split(' - ')
  const cleaned = (parts.length > 1 ? parts.slice(1).join(' - ') : raw).trim()
  return cleaned || null
}

function currentRoute(pathname: string, searchParams: { toString(): string } | null): string {
  const search = searchParams?.toString() ?? ''
  return normalizeAppPath(search ? `${pathname}?${search}` : pathname)
}

function pathWithoutSearch(path: string): string {
  const index = path.indexOf('?')
  return index === -1 ? path : path.slice(0, index)
}

/** Label used until the page resolves its title: the humanized first segment. */
function fallbackTabTitle(path: string): string {
  const segment = path.split('/').find(Boolean) ?? 'dashboard'
  const words = segment.split('-').join(' ')
  return words.charAt(0).toUpperCase() + words.slice(1)
}

// The whole bar is a window-drag region; interactive children opt back out.
// `WebkitAppRegion` is Electron-only CSS — inert in a browser (where this
// component doesn't render anyway).
const Bar = styled('header')(({ theme }) => ({
  WebkitAppRegion: 'drag',
  height: TITLE_BAR_HEIGHT,
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(1),
  paddingLeft: TRAFFIC_LIGHT_INSET,
  paddingRight: theme.spacing(3),
  backgroundColor: theme.palette.background.layout,
  borderBottom: `1px solid ${theme.palette.divider}`
}))

const NoDragIconButton = styled(IconButton)({
  WebkitAppRegion: 'no-drag'
})

// no-drag so clicks, middle-clicks, and horizontal trackpad scrolling reach
// the chips; the spare width to the right of it stays a drag region.
const TabsRow = styled('div')(({ theme }) => ({
  WebkitAppRegion: 'no-drag',
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(1),
  marginLeft: theme.spacing(2),
  minWidth: 0,
  overflowX: 'auto',
  scrollbarWidth: 'none'
}))

const TabChip = styled('div', { shouldForwardProp: (prop) => prop !== 'active' })<{ active: boolean }>(
  ({ theme, active }) => ({
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
    maxWidth: 200,
    padding: theme.spacing(1, 2),
    borderRadius: theme.spacing(1.5),
    cursor: 'default',
    userSelect: 'none',
    whiteSpace: 'nowrap',
    border: `1px solid ${active ? theme.palette.divider : 'transparent'}`,
    backgroundColor: active ? theme.palette.background.paper : 'transparent',
    '&:hover': {
      backgroundColor: active ? theme.palette.background.paper : theme.palette.action.hover
    },
    '&:focus-visible': {
      outline: `2px solid ${theme.palette.primary.main}`,
      outlineOffset: 1
    }
  })
)

const TabLabel = styled('span')(({ theme }) => ({
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  fontSize: theme.typography.body2.fontSize
}))

const TabCloseButton = styled(IconButton)({
  padding: 2
})
